import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    rootElementSelector: String,
  };
  static targets = ["navigationContainer", "section", "navigationLink"];

  connect() {
    document.addEventListener("turbo:morph", this.restartObserver);
    // Use requestAnimationFrame to ensure DOM is ready after morphing
    requestAnimationFrame(() => {
      this.#startObserver();
    });
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.restartObserver);
    if (this.observerForSettingActiveOnSectionNavigationsLinkTargets) {
      this.observerForSettingActiveOnSectionNavigationsLinkTargets.disconnect();
    }
  }

  // Done when scrolling page vertically
  //
  // When the page is scrolled vertically, and a section is intersected, it sets
  // the active link on the navigation and scrolls the navigation horizontally
  // to it.
  async handleIntersectionOfPageSectionTargetWhenScrollingPageVertically(
    pageSectionEntries
  ) {
    pageSectionEntries.forEach(async (pageSectionEntry) => {
      if (pageSectionEntry.isIntersecting) {
        // Find the link that corresponds to the intersecting section
        const activeLinksMap = new Map();

        this.navigationContainerTargets.forEach((navigationContainer) => {
          const navigationContainerActiveLinks =
            navigationContainer.querySelectorAll(
              "[data-scroll-spy-target='navigationLink']"
            );

          navigationContainerActiveLinks.forEach((navigationLink) => {
            if (
              navigationLink.getAttribute("href") ===
              `#${pageSectionEntry.target.id}`
            ) {
              const value = activeLinksMap.get(navigationContainer) || [];

              activeLinksMap.set(navigationContainer, [
                ...value,
                navigationLink,
              ]);
            }
          });
        });

        if (!activeLinksMap.size) {
          return;
        }

        activeLinksMap.forEach((activeLinks, navigationContainer) => {
          activeLinks.forEach((activeLink) => {
            this.#setActiveOnIntersectingNavigationLinkTarget(
              activeLink,
              navigationContainer
            );
          });
        });

        // If the page is already scrolling vertically to a section, we wait for
        // the scroll to end before scrolling the navigation horizontally to the
        // active link.
        //
        // This is to prevent the browser from conflicting between the page's
        // vertical scroll and the navigation's horizontal scroll.
        if (this.isScrollingPageVerticallyToSection) {
          await this.#waitForScrollEnd();
        }

        // Scroll to the active link
        activeLinksMap.forEach((activeLinks, navigationContainer) => {
          activeLinks.forEach((activeLink) => {
            this.#scrollNavigationHorizontallyToNavigationLink(
              activeLink,
              navigationContainer
            );
          });
        });
      }
    });
  }

  // Done on click on a section navigation link
  //
  // It scrolls the page to the section and sets the active link on the
  // navigation.
  async scrollPageToSection(event) {
    event.preventDefault();
    const targetId = event.currentTarget.getAttribute("href");
    const targetElement = document.querySelector(targetId);

    // Calculate scroll position with offset based on viewport
    const navigationHeight = this.navigationContainerTarget.offsetHeight;
    const isMobile = window.innerWidth < 768; // md breakpoint in Tailwind
    // On mobile, the navigation is at the bottom, so no need to offset the
    // scroll. On desktop, the navigation is fixed at the top, so we need to
    // scroll a bit more so that the section is visible and is not covered by
    // the navigation.
    const scrollOffset = isMobile ? navigationHeight : navigationHeight + 64;

    const scrollableElementRect =
      this.scrollableElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const targetPosition =
      targetRect.top -
      scrollableElementRect.top +
      this.scrollableElement.scrollTop -
      scrollOffset;

    // Before scrolling the page vertically to the section, we set a flag to
    // prevent the navigation from being scrolled horizontally when the page is
    // scrolled vertically. Which would cause the browser to conflict between the
    // two scrollings and not scroll the page at all.
    this.isScrollingPageVerticallyToSection = true;

    this.scrollableElement.scrollTo({
      top: targetPosition,
      behavior: "smooth",
    });

    await this.#waitForScrollEnd();
    this.isScrollingPageVerticallyToSection = false;
  }

  // private

  get isMobile() {
    return window.innerWidth < 768;
  }

  get thresholds() {
    if (this.isMobile) {
      return 0.1;
    } else {
      return 0.5;
    }
  }

  #startObserver = () => {
    this.observerForSettingActiveOnSectionNavigationsLinkTargets =
      new IntersectionObserver(
        this.handleIntersectionOfPageSectionTargetWhenScrollingPageVertically.bind(
          this
        ),
        {
          root: this.hasRootElementSelectorValue
            ? document.querySelector(this.rootElementSelectorValue)
            : null,
          rootMargin: "0px",
          threshold: this.thresholds,
        }
      );

    this.sectionTargets.forEach((section) =>
      this.observerForSettingActiveOnSectionNavigationsLinkTargets.observe(
        section
      )
    );
  };

  restartObserver = () => {
    if (this.observerForSettingActiveOnSectionNavigationsLinkTargets) {
      this.observerForSettingActiveOnSectionNavigationsLinkTargets.disconnect();
      this.observerForSettingActiveOnSectionNavigationsLinkTargets = null;
    }

    // Wait for DOM to stabilize after morphing
    requestAnimationFrame(() => {
      this.#startObserver();
    });
  };

  #setActiveOnIntersectingNavigationLinkTarget(
    activeLink,
    navigationContainer
  ) {
    // Remove active class from all links
    navigationContainer
      .querySelectorAll("[data-scroll-spy-target='navigationLink']")
      .forEach((link) => link.classList.remove("btn-active"));

    // Add active class to the current link
    activeLink.classList.add("btn-active");
  }

  #scrollNavigationHorizontallyToNavigationLink(
    navigationLink,
    navigationContainer
  ) {
    navigationContainer.scrollTo({
      left: navigationLink.offsetLeft - 100,
      behavior: "smooth",
    });
  }

  #waitForScrollEnd() {
    let lastChangedFrame = 0;
    let lastX = this.scrollableElement.scrollLeft;
    let lastY = this.scrollableElement.scrollTop;
    const STATIC_FRAMES_THRESHOLD = 20;
    const MAX_FRAMES = 180; // 3 seconds at 60fps

    return new Promise((resolve) => {
      const tick = (frames) => {
        if (
          frames >= MAX_FRAMES ||
          frames - lastChangedFrame > STATIC_FRAMES_THRESHOLD
        ) {
          resolve();
        } else {
          if (
            this.scrollableElement.scrollLeft !== lastX ||
            this.scrollableElement.scrollTop !== lastY
          ) {
            lastChangedFrame = frames;
            lastX = this.scrollableElement.scrollLeft;
            lastY = this.scrollableElement.scrollTop;
          }
          requestAnimationFrame(() => tick(frames + 1));
        }
      };
      requestAnimationFrame(() => tick(0));
    });
  }

  get scrollableElement() {
    if (this.hasRootElementSelectorValue) {
      return document.querySelector(this.rootElementSelectorValue);
    } else {
      return document.querySelector("main");
    }
  }
}
